import { type KeyboardEvent, type PointerEvent, useState } from "react"
import type { DashboardDay } from "@/api.js"
import { Table, Td, Th, Tr } from "@/components/ui/table.js"
import { plural } from "@/lib/plural.js"
import { cn } from "@/lib/utils.js"
import {
  count,
  dayMonth,
  isWeekend,
  labelledDays,
  longDay,
  niceScale,
  share,
  weekday,
} from "./format.js"
import { useWidth } from "./useWidth.js"

/**
 * Decisions, day by day: a column per Warsaw day, and beneath it — on the same
 * days — the share of each day that was prepaid, and the answers that came
 * from fail-closed rather than from data.
 *
 * **Small multiples on one axis, never two scales on one plot.** Prepayment is
 * a few percent of a day and degraded answers a handful against hundreds:
 * inside the column they are a sliver nobody can compare, and on a second
 * scale they would invent a relationship. A strip each, sharing only the days,
 * keeps all three readable and honest.
 */

/**
 * Stacked bottom to top. **Prepayment sits on the baseline** — a series sharing
 * one baseline can be compared day against day, a cap floating on top of four
 * hundred allowed answers cannot. `REVIEW` is reserved and drawn only on a
 * window that has one.
 */
const SERIES = [
  { key: "prepayment", label: "Przedpłata", fill: "var(--prepay)" },
  { key: "allow", label: "Odroczenie", fill: "var(--chart-allow)" },
  { key: "review", label: "Do decyzji", fill: "var(--review)" },
] as const
type Series = readonly (typeof SERIES)[number][]

const DEGRADED = { label: "Niepewne", fill: "var(--critical)" } as const

const TOP = 10
const PLOT = 196
/** Room above each strip for its own title. */
const TITLE = 30
const AXIS = 26
const LEFT = 44
const RIGHT = 4
/** The surface gap between stacked segments, so neighbours read as two things. */
const GAP = 2
/** A non-zero value never disappears into the baseline. */
const MIN_SEGMENT = 2
const TOOLTIP_WIDTH = 220

/** A panel under the columns: one value a day, on a scale of its own. */
interface Strip {
  readonly key: string
  readonly title: string
  readonly summary: string
  readonly height: number
  readonly fill: string
  /** Null where the day has nothing to measure — no bar, which is not a zero. */
  readonly values: readonly (number | null)[]
  readonly max: number
  readonly maxLabel: string
}

function stripsOf(days: readonly DashboardDay[]): Strip[] {
  const decided = days.reduce((sum, day) => sum + totalOf(day), 0)
  const prepaid = days.reduce((sum, day) => sum + day.prepayment, 0)
  const degraded = days.reduce((sum, day) => sum + day.degraded, 0)
  const shares = days.map((day) => (totalOf(day) === 0 ? null : day.prepayment / totalOf(day)))
  const highestShare = Math.max(...shares.map((value) => value ?? 0))
  const highestDegraded = Math.max(...days.map((day) => day.degraded))

  return [
    {
      key: "share",
      title: "Przedpłata, udział dnia",
      summary: `${share(prepaid, decided) ?? "—"} w całym okresie`,
      height: 40,
      fill: "var(--prepay)",
      values: shares,
      max: highestShare > 0 ? highestShare : 1,
      maxLabel: highestShare > 0 ? (share(highestShare, 1) ?? "") : "",
    },
    {
      key: "degraded",
      title: DEGRADED.label,
      summary: degraded === 0 ? "żadnych w tym okresie" : `${count(degraded)} w tym okresie`,
      height: 34,
      fill: DEGRADED.fill,
      values: days.map((day) => day.degraded),
      max: Math.max(highestDegraded, 1),
      maxLabel: highestDegraded > 0 ? count(highestDegraded) : "",
    },
  ]
}

export function DailyChart({ days }: { days: readonly DashboardDay[] }) {
  const [table, setTable] = useState(false)
  const review = days.some((day) => day.review > 0)
  const series = SERIES.filter((entry) => entry.key !== "review" || review)

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h3 className="text-[15px] font-semibold text-foreground">Decyzje dzień po dniu</h3>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5">
          <ul className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-muted-foreground">
            {[...series, DEGRADED].map((entry) => (
              <li key={entry.label} className="inline-flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className="size-2.5 rounded-[2px]"
                  style={{ background: entry.fill }}
                />
                {entry.label}
              </li>
            ))}
          </ul>
          {/* The chart's accessible twin: every value it draws, as rows. */}
          <button
            type="button"
            aria-pressed={table}
            onClick={() => setTable(!table)}
            className="text-[13px] text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline"
          >
            {table ? "Wykres" : "Tabela"}
          </button>
        </div>
      </div>

      {table ? <DayTable days={days} review={review} /> : <Plot days={days} series={series} />}
    </section>
  )
}

/** Where everything sits, for one set of days at one width. */
interface Layout {
  readonly width: number
  readonly slot: number
  readonly bar: number
  readonly scale: { readonly top: number; readonly ticks: readonly number[] }
  readonly baseline: number
  /** Each strip with the y of its baseline. */
  readonly strips: readonly (Strip & { readonly bottom: number })[]
  readonly height: number
  readonly last: number
}

function layout(days: readonly DashboardDay[], width: number): Layout {
  const slot = Math.max(width - LEFT - RIGHT, 1) / Math.max(days.length, 1)
  const baseline = TOP + PLOT
  let cursor = baseline
  const strips = stripsOf(days).map((strip) => {
    cursor += TITLE + strip.height
    return { ...strip, bottom: cursor }
  })
  return {
    width,
    slot,
    // Thin marks: never wider than 24px, never the whole slot — the leftover is air.
    bar: Math.min(24, Math.max(2, slot * 0.62)),
    scale: niceScale(Math.max(...days.map(totalOf), 0)),
    baseline,
    strips,
    height: cursor + AXIS,
    last: days.length - 1,
  }
}

const slotLeft = (at: Layout, index: number) => LEFT + index * at.slot
const barLeft = (at: Layout, index: number) => slotLeft(at, index) + (at.slot - at.bar) / 2

/**
 * The plot, and the one day it is pointing at. **A slider over the days**, as
 * far as a keyboard and a screen reader are concerned: the arrows step through
 * them and each day is read out as the slider's value — the same numbers the
 * tooltip shows, and the table view holds all of them at once.
 */
function Plot({ days, series }: { days: readonly DashboardDay[]; series: Series }) {
  const [container, width] = useWidth<HTMLDivElement>(960)
  const [active, setActive] = useState<number | null>(null)
  const at = layout(days, width)
  const shown = active === null ? undefined : days[active]
  const lowest = at.strips.at(-1)?.bottom ?? at.baseline

  function pick(event: PointerEvent<SVGSVGElement>) {
    const box = event.currentTarget.getBoundingClientRect()
    const index = Math.floor((event.clientX - box.left - LEFT) / at.slot)
    setActive(Math.min(Math.max(index, 0), at.last))
  }

  function step(event: KeyboardEvent<HTMLDivElement>) {
    const moves: Record<string, (current: number) => number> = {
      ArrowLeft: (current) => current - 1,
      ArrowDown: (current) => current - 1,
      ArrowRight: (current) => current + 1,
      ArrowUp: (current) => current + 1,
      Home: () => 0,
      End: () => at.last,
    }
    const move = moves[event.key]
    if (move === undefined) return
    event.preventDefault()
    setActive((current) => Math.min(Math.max(move(current ?? at.last), 0), at.last))
  }

  return (
    <div className="rounded-lg border bg-card px-4 pt-4 pb-2 shadow-xs">
      <div
        ref={container}
        tabIndex={0}
        role="slider"
        aria-label="Dzień na wykresie decyzji"
        aria-valuemin={0}
        aria-valuemax={at.last}
        aria-valuenow={active ?? at.last}
        aria-valuetext={spoken(days[active ?? at.last], active === null || active === at.last)}
        onKeyDown={step}
        onFocus={() => setActive((current) => current ?? at.last)}
        onBlur={() => setActive(null)}
        className="relative rounded-md focus-visible:outline-offset-4"
      >
        <svg
          width={width}
          height={at.height}
          aria-hidden="true"
          className="block touch-none select-none"
          onPointerMove={pick}
          onPointerLeave={() => setActive(null)}
        >
          {active !== null && (
            <rect
              x={slotLeft(at, active)}
              y={TOP}
              width={at.slot}
              height={lowest - TOP}
              rx={3}
              fill="var(--muted)"
            />
          )}
          <Grid at={at} />
          <Columns days={days} series={series} at={at} />
          {at.strips.map((strip) => (
            <StripPanel key={strip.key} strip={strip} at={at} />
          ))}
          <Axis days={days} at={at} below={lowest} />
        </svg>

        {shown !== undefined && active !== null && (
          <Readout
            day={shown}
            series={series}
            today={active === at.last}
            left={tooltipLeft(slotLeft(at, active) + at.slot / 2, width)}
          />
        )}
      </div>
    </div>
  )
}

/** Hairlines at round numbers, the baseline a step darker. */
function Grid({ at }: { at: Layout }) {
  return (
    <>
      {at.scale.ticks.map((tick) => {
        const y = at.baseline - (tick / at.scale.top) * PLOT
        return (
          <g key={tick}>
            <line
              x1={LEFT}
              x2={at.width - RIGHT}
              y1={y}
              y2={y}
              stroke={tick === 0 ? "var(--input)" : "var(--border)"}
              shapeRendering="crispEdges"
            />
            <text
              x={LEFT - 8}
              y={y}
              textAnchor="end"
              dominantBaseline="middle"
              className="fill-muted-foreground text-[11px] tabular-nums"
            >
              {count(tick)}
            </text>
          </g>
        )
      })}
    </>
  )
}

function Columns({
  days,
  series,
  at,
}: {
  days: readonly DashboardDay[]
  series: Series
  at: Layout
}) {
  return (
    <>
      {days.map((day, index) =>
        stack(day, series, at.scale.top).map((segment) => {
          const x = barLeft(at, index)
          const y = at.baseline - segment.to
          const height = segment.to - segment.from
          return (
            <path
              key={`${day.date}-${segment.key}`}
              d={segment.top ? roundedTop(x, y, at.bar, height) : rect(x, y, at.bar, height)}
              fill={segment.fill}
            />
          )
        }),
      )}
    </>
  )
}

/** One strip: its title, its busiest value as the only tick, and a bar a day. */
function StripPanel({ strip, at }: { strip: Layout["strips"][number]; at: Layout }) {
  const top = strip.bottom - strip.height
  return (
    <g>
      <text x={LEFT} y={top - 10} className="fill-muted-foreground text-[11.5px]">
        <tspan className="fill-foreground font-medium">{strip.title}</tspan>
        <tspan dx={6}>{strip.summary}</tspan>
      </text>
      {strip.maxLabel !== "" && (
        <text
          x={LEFT - 8}
          y={top + 4}
          textAnchor="end"
          dominantBaseline="middle"
          className="fill-muted-foreground text-[11px] tabular-nums"
        >
          {strip.maxLabel}
        </text>
      )}
      <line
        x1={LEFT}
        x2={at.width - RIGHT}
        y1={strip.bottom}
        y2={strip.bottom}
        stroke="var(--input)"
        shapeRendering="crispEdges"
      />
      {strip.values.map((value, index) => {
        if (value === null || value === 0) return null
        const height = Math.max((value / strip.max) * strip.height, MIN_SEGMENT)
        return (
          <path
            // biome-ignore lint/suspicious/noArrayIndexKey: a bar is its day, and the days are the axis
            key={index}
            d={roundedTop(barLeft(at, index), strip.bottom - height, at.bar, height)}
            fill={strip.fill}
          />
        )
      })}
    </g>
  )
}

/** Days along the bottom: as many as fit, today always called today. */
function Axis({ days, at, below }: { days: readonly DashboardDay[]; at: Layout; below: number }) {
  const labelled = labelledDays(
    days.map((day) => day.date),
    at.slot,
  )
  const everyDay = labelled.size === days.length
  return (
    <>
      {days.map((day, index) => {
        if (!labelled.has(index)) return null
        const today = index === at.last
        return (
          <text
            key={`${day.date}-label`}
            x={slotLeft(at, index) + at.slot / 2}
            y={below + 17}
            textAnchor="middle"
            className={cn(
              "text-[11px] tabular-nums",
              today && "fill-foreground font-medium",
              !today &&
                (isWeekend(day.date) ? "fill-muted-foreground/60" : "fill-muted-foreground"),
            )}
          >
            {today
              ? "dziś"
              : everyDay
                ? `${weekday(day.date)} ${dayMonth(day.date)}`
                : dayMonth(day.date)}
          </text>
        )
      })}
    </>
  )
}

/** Beside the column, and on its other side once the card runs out. */
function tooltipLeft(center: number, width: number): number {
  const right = center + 14
  return right + TOOLTIP_WIDTH <= width ? right : Math.max(center - 14 - TOOLTIP_WIDTH, 0)
}

/**
 * The day under the pointer, every series at once. **Values lead**: the
 * reader already knows which series they are looking for and wants its number.
 */
function Readout({
  day,
  series,
  today,
  left,
}: {
  day: DashboardDay
  series: Series
  today: boolean
  left: number
}) {
  const total = totalOf(day)
  const rows = [
    ...series.map((entry) => ({
      key: entry.key,
      fill: entry.fill,
      value: day[entry.key],
      label: entry.label.toLowerCase(),
      ratio: entry.key === "prepayment" ? share(day.prepayment, total) : null,
    })),
    { key: "degraded", fill: DEGRADED.fill, value: day.degraded, label: "niepewne", ratio: null },
  ]

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute top-1 z-10 rounded-md border bg-popover px-3 py-2.5 text-[13px] shadow-md"
      style={{ left, width: TOOLTIP_WIDTH }}
    >
      <p className="mb-1.5 font-medium text-foreground">
        {longDay(day.date)}
        {today && <span className="font-normal text-muted-foreground"> · dziś</span>}
      </p>
      <ul className="space-y-0.5">
        {rows.map((row) => (
          <li key={row.key} className="flex items-center gap-2">
            <span
              aria-hidden="true"
              className="h-0.5 w-3 shrink-0 rounded-full"
              style={{ background: row.fill }}
            />
            <span className="min-w-8 font-semibold tabular-nums text-foreground">
              {count(row.value)}
            </span>
            <span className="text-muted-foreground">{row.label}</span>
            {row.ratio !== null && (
              <span className="ml-auto tabular-nums text-muted-foreground">{row.ratio}</span>
            )}
          </li>
        ))}
      </ul>
      <p className="mt-1.5 border-t pt-1.5 text-muted-foreground">
        <span className="font-semibold tabular-nums text-foreground">{count(total)}</span>{" "}
        {plural(total, "decyzja", "decyzje", "decyzji")} ·{" "}
        <span className="font-semibold tabular-nums text-foreground">{count(day.taxpayers)}</span>{" "}
        {plural(day.taxpayers, "podmiot", "podmioty", "podmiotów")}
      </p>
    </div>
  )
}

/** What a screen reader hears for the day the arrows landed on. */
function spoken(day: DashboardDay | undefined, today: boolean): string {
  if (day === undefined) return ""
  return [
    `${longDay(day.date)}${today ? ", dziś" : ""}`,
    `odroczenie ${day.allow}`,
    `przedpłata ${day.prepayment}`,
    `niepewne ${day.degraded}`,
    `podmioty ${day.taxpayers}`,
  ].join(", ")
}

function DayTable({ days, review }: { days: readonly DashboardDay[]; review: boolean }) {
  return (
    <Table>
      <thead>
        <tr>
          <Th>Dzień</Th>
          <Th className="text-right">Odroczenie</Th>
          <Th className="text-right">Przedpłata</Th>
          <Th className="text-right">Udział przedpłat</Th>
          {review && <Th className="text-right">Do decyzji</Th>}
          <Th className="text-right">Niepewne</Th>
          <Th className="text-right">Podmioty</Th>
          <Th className="text-right">Razem</Th>
        </tr>
      </thead>
      <tbody>
        {[...days].reverse().map((day) => (
          <Tr key={day.date}>
            <Td className="whitespace-nowrap font-mono text-[13px] text-muted-foreground">
              {day.date} <span className="font-sans">{weekday(day.date)}</span>
            </Td>
            <Td className="text-right tabular-nums">{count(day.allow)}</Td>
            <Td className="text-right tabular-nums">{count(day.prepayment)}</Td>
            <Td className="text-right tabular-nums text-muted-foreground">
              {share(day.prepayment, totalOf(day)) ?? "—"}
            </Td>
            {review && <Td className="text-right tabular-nums">{count(day.review)}</Td>}
            <Td
              className={cn(
                "text-right tabular-nums",
                day.degraded > 0 && "font-medium text-critical",
              )}
            >
              {count(day.degraded)}
            </Td>
            <Td className="text-right tabular-nums">{count(day.taxpayers)}</Td>
            <Td className="text-right font-medium tabular-nums">{count(totalOf(day))}</Td>
          </Tr>
        ))}
      </tbody>
    </Table>
  )
}

function totalOf(day: DashboardDay): number {
  return day.allow + day.prepayment + day.review
}

/**
 * One column's segments, in pixels above the baseline. Each segment above the
 * first gives up its bottom 2px to the gap, so the column's height still says
 * the day's total; a value too small to see is drawn at the minimum instead of
 * vanishing.
 */
function stack(
  day: DashboardDay,
  series: Series,
  top: number,
): { key: string; fill: string; from: number; to: number; top: boolean }[] {
  const segments: { key: string; fill: string; from: number; to: number; top: boolean }[] = []
  let cursor = 0
  for (const entry of series) {
    const value = day[entry.key]
    if (value === 0) continue
    const height = (value / top) * PLOT
    const from = segments.length === 0 ? cursor : cursor + GAP
    const to = Math.max(cursor + height, from + MIN_SEGMENT)
    segments.push({ key: entry.key, fill: entry.fill, from, to, top: false })
    cursor = to
  }
  const highest = segments.at(-1)
  if (highest !== undefined) highest.top = true
  return segments
}

function rect(x: number, y: number, width: number, height: number): string {
  return `M${x},${y}h${width}v${height}h${-width}Z`
}

/** Rounded at the data end, square on the baseline. */
function roundedTop(x: number, y: number, width: number, height: number): string {
  const r = Math.min(4, width / 2, height)
  return `M${x},${y + height}V${y + r}A${r},${r} 0 0 1 ${x + r},${y}H${x + width - r}A${r},${r} 0 0 1 ${x + width},${y + r}V${y + height}Z`
}
